import moment from 'moment';
import { Nullable } from 'global';
import { NO_DATA, DateFormats } from 'constants/common';

import { EventDue } from '../types';

export type DueTone = 'overdue' | 'urgent' | 'soon' | 'upcoming' | 'none';

export interface DueView {
  tone: DueTone;
  label: string;
  dateText: string;
}

const SECONDS_IN = { minute: 60, hour: 3600, day: 86400 } as const;

const DURATION_UNITS = [
  { suffix: 'd', seconds: SECONDS_IN.day },
  { suffix: 'h', seconds: SECONDS_IN.hour },
  { suffix: 'm', seconds: SECONDS_IN.minute },
] as const;

const UPCOMING_TIERS: { maxSeconds: number; tone: DueTone }[] = [
  { maxSeconds: SECONDS_IN.hour, tone: 'urgent' },
  { maxSeconds: SECONDS_IN.day, tone: 'soon' },
  { maxSeconds: Infinity, tone: 'upcoming' },
];

export const formatDuration = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));

  if (seconds < SECONDS_IN.minute) return '< 1m';

  const { chunks } = DURATION_UNITS.reduce<{ rest: number; chunks: string[] }>(
    (acc, unit) => {
      const value = Math.floor(acc.rest / unit.seconds);

      return {
        rest: acc.rest - value * unit.seconds,
        chunks: value > 0 ? [...acc.chunks, `${value}${unit.suffix}`] : acc.chunks,
      };
    },
    { rest: seconds, chunks: [] }
  );

  return chunks.slice(0, 2).join(' ');
};

export const resolveDueView = (due: Nullable<EventDue>, allDay = false): DueView => {
  if (!due?.dueAt) return { tone: 'none', label: NO_DATA, dateText: NO_DATA };

  const dateText = allDay
    ? `${moment(due.dueAt).format(DateFormats.Medium)} All Day`
    : moment(due.dueAt).format(DateFormats.DateTimeMedium12Hour);

  if (due.isOverdue) {
    return { tone: 'overdue', dateText, label: `Overdue by ${formatDuration(due.secondsSinceDue ?? 0)}` };
  }

  const secondsUntilDue = due.secondsUntilDue ?? 0;
  const { tone } = UPCOMING_TIERS.find(tier => secondsUntilDue <= tier.maxSeconds)!;

  return { tone, dateText, label: `Due in ${formatDuration(secondsUntilDue)}` };
};
